import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  User,
  Mail,
  Phone,
  Calendar,
  Award,
  Star,
  Clock,
  MapPin,
  Users,
  CheckCircle,
  AlertCircle,
  X,
  History,
  FileText,
  Save,
  Link,
  Unlink,
  Search,
  UserCheck
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { compressImage } from '../../utils/imageUtils';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db, storage } from '../../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useBusiness } from '../../contexts/BusinessContext';
import { useSystemConfig } from '../../hooks/useSystemConfig';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Loading } from '../../components/ui/Loading';

interface Instructor {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  bio?: string;
  specialties: string[];
  experience: number; // años de experiencia
  certifications: string[];
  isActive: boolean;
  hireDate: string;
  avatar?: string;
  rating: number;
  totalClasses: number;
  totalStudents: number;
  businessId: string;
  classTypeIds?: string[]; // IDs de las clases que imparte
  notes?: string; // Anotaciones del administrador
  createdAt: any;
  updatedAt: any;
}

interface ClassType {
  id: string;
  name: string;
  color: string;
  businessId: string;
}

interface ClassHistory {
  id: string;
  name: string;
  date: string;
  time: string;
  enrolled: number;
  maxCapacity: number;
  location?: string;
}

const InstructorsPage: React.FC = () => {
  const { businessId, currentBusiness } = useBusiness();
  const { config: systemConfig } = useSystemConfig();
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [classTypes, setClassTypes] = useState<ClassType[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedInstructor, setSelectedInstructor] = useState<Instructor | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    bio: '',
    specialtiesText: '', // Texto libre, se convierte a array al guardar
    experience: 0,
    certificationsText: '', // Texto libre, se convierte a array al guardar
    isActive: true,
    classTypeIds: [] as string[], // IDs de las clases asignadas
  });
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');

  // Estados para el panel de detalles
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [detailInstructor, setDetailInstructor] = useState<Instructor | null>(null);
  const [classHistory, setClassHistory] = useState<ClassHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [instructorNotes, setInstructorNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  // Estados para vinculación de usuario
  const [linkEmail, setLinkEmail] = useState('');
  const [searchingUser, setSearchingUser] = useState(false);
  const [linkingUser, setLinkingUser] = useState(false);
  const [linkedUserData, setLinkedUserData] = useState<{ id: string; firstName: string; lastName: string; email: string } | null>(null);

  // Cargar instructores desde Firebase
  const loadInstructors = async () => {
    if (!businessId) return;

    try {
      setLoading(true);
      console.log('🔄 Cargando instructores desde Firebase...');

      // Evitar depender de índice compuesto mientras se construye: quitar orderBy y ordenar en cliente
      const instructorsQuery = query(
        collection(db, 'instructors'),
        where('businessId', '==', businessId)
      );
      const instructorsSnapshot = await getDocs(instructorsQuery);
      const instructorsData = instructorsSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }) as Instructor)
        .sort((a, b) => {
          const toMillis = (v: any) => {
            if (!v) return 0;
            if (typeof v?.toDate === 'function') return v.toDate().getTime();
            if (v instanceof Date) return v.getTime();
            // ISO string o numérico
            return new Date(v).getTime() || 0;
          };
          return toMillis(b.createdAt) - toMillis(a.createdAt);
        });

      setInstructors(instructorsData);
      console.log(`✅ ${instructorsData.length} instructores cargados`);

      // Cargar tipos de clase
      const classTypesQuery = query(
        collection(db, 'classTypes'),
        where('businessId', '==', businessId)
      );
      const classTypesSnapshot = await getDocs(classTypesQuery);
      const classTypesData = classTypesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ClassType[];
      setClassTypes(classTypesData);
      console.log(`✅ ${classTypesData.length} tipos de clase cargados`);

    } catch (error) {
      console.error('❌ Error cargando instructores:', error);
      toast.error('Error al cargar los instructores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!businessId) {
      setLoading(false);
      return;
    }
    loadInstructors();
  }, [businessId]);

  // Cargar historial de clases del instructor
  const loadInstructorHistory = async (instructorId: string) => {
    if (!businessId) return;

    try {
      setLoadingHistory(true);

      // Buscar todas las clases donde este instructor fue asignado
      const classesQuery = query(
        collection(db, 'classes'),
        where('businessId', '==', businessId),
        where('instructorId', '==', instructorId)
      );
      const classesSnapshot = await getDocs(classesQuery);

      const historyData: ClassHistory[] = classesSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          date: data.date,
          time: data.time,
          enrolled: Object.keys(data.enrolled || {}).length,
          maxCapacity: data.maxCapacity,
          location: data.location
        };
      });

      // Ordenar por fecha (más recientes primero)
      historyData.sort((a, b) => {
        const dateA = new Date(`${a.date}T${a.time}`);
        const dateB = new Date(`${b.date}T${b.time}`);
        return dateB.getTime() - dateA.getTime();
      });

      setClassHistory(historyData);
    } catch (error) {
      console.error('Error cargando historial:', error);
      toast.error('Error al cargar el historial de clases');
    } finally {
      setLoadingHistory(false);
    }
  };

  // Abrir panel de detalles del instructor
  const handleViewInstructorDetails = async (instructor: Instructor) => {
    setDetailInstructor(instructor);
    setInstructorNotes(instructor.notes || '');
    setLinkEmail('');
    setLinkedUserData(null);
    setShowDetailPanel(true);
    await loadInstructorHistory(instructor.id);

    // Cargar usuario vinculado si existe
    if ((instructor as any).userId) {
      await loadLinkedUser((instructor as any).userId);
    }
  };

  // Guardar notas del instructor
  const handleSaveNotes = async () => {
    if (!detailInstructor) return;

    try {
      setSavingNotes(true);
      await updateDoc(doc(db, 'instructors', detailInstructor.id), {
        notes: instructorNotes,
        updatedAt: serverTimestamp()
      });

      // Actualizar el instructor en la lista local
      setInstructors(prev => prev.map(inst =>
        inst.id === detailInstructor.id
          ? { ...inst, notes: instructorNotes }
          : inst
      ));
      setDetailInstructor(prev => prev ? { ...prev, notes: instructorNotes } : null);

      toast.success('Anotaciones guardadas');
    } catch (error) {
      console.error('Error guardando notas:', error);
      toast.error('Error al guardar las anotaciones');
    } finally {
      setSavingNotes(false);
    }
  };

  // Cargar datos del usuario vinculado
  const loadLinkedUser = async (userId: string) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setLinkedUserData({
          id: userDoc.id,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email
        });
      }
    } catch (error) {
      console.error('Error cargando usuario vinculado:', error);
    }
  };

  // Buscar usuario por email para vincular
  const handleSearchUser = async () => {
    if (!linkEmail.trim() || !businessId) return;

    try {
      setSearchingUser(true);
      const usersQuery = query(
        collection(db, 'users'),
        where('businessId', '==', businessId),
        where('email', '==', linkEmail.trim().toLowerCase())
      );
      const snapshot = await getDocs(usersQuery);

      if (snapshot.empty) {
        toast.error('No se encontró ningún usuario con ese email');
        return;
      }

      const userData = snapshot.docs[0].data();
      const userId = snapshot.docs[0].id;

      // Verificar que no esté ya vinculado a otro instructor
      const existingInstructor = instructors.find(i =>
        (i as any).userId === userId && i.id !== detailInstructor?.id
      );
      if (existingInstructor) {
        toast.error(`Este usuario ya está vinculado al instructor ${existingInstructor.firstName} ${existingInstructor.lastName}`);
        return;
      }

      // Verificar rol actual
      if (userData.role !== 'user' && userData.role !== 'instructor') {
        toast.error('Este usuario tiene un rol de administrador y no puede ser instructor');
        return;
      }

      setLinkedUserData({
        id: userId,
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email
      });

    } catch (error) {
      console.error('Error buscando usuario:', error);
      toast.error('Error al buscar el usuario');
    } finally {
      setSearchingUser(false);
    }
  };

  // Vincular usuario al instructor
  const handleLinkUser = async () => {
    if (!detailInstructor || !linkedUserData) return;

    try {
      setLinkingUser(true);

      // Actualizar instructor con userId
      await updateDoc(doc(db, 'instructors', detailInstructor.id), {
        userId: linkedUserData.id,
        updatedAt: serverTimestamp()
      });

      // Actualizar usuario con rol instructor e instructorId
      // También copiar la foto del instructor si existe
      const userUpdate: any = {
        role: 'instructor',
        instructorId: detailInstructor.id,
        updatedAt: serverTimestamp()
      };

      // Si el instructor tiene avatar, copiarlo al usuario
      if (detailInstructor.avatar) {
        userUpdate.profilePhotoUrl = detailInstructor.avatar;
      }

      await updateDoc(doc(db, 'users', linkedUserData.id), userUpdate);

      // Actualizar estado local
      setInstructors(prev => prev.map(inst =>
        inst.id === detailInstructor.id
          ? { ...inst, userId: linkedUserData.id } as Instructor
          : inst
      ));
      setDetailInstructor(prev => prev ? { ...prev, userId: linkedUserData.id } as Instructor : null);

      toast.success(`Usuario ${linkedUserData.firstName} vinculado correctamente`);
      setLinkEmail('');

    } catch (error) {
      console.error('Error vinculando usuario:', error);
      toast.error('Error al vincular el usuario');
    } finally {
      setLinkingUser(false);
    }
  };

  // Desvincular usuario del instructor
  const handleUnlinkUser = async () => {
    if (!detailInstructor || !(detailInstructor as any).userId) return;

    const userId = (detailInstructor as any).userId;

    if (!confirm('¿Estás seguro de desvincular este usuario? Perderá acceso al panel de instructor.')) {
      return;
    }

    try {
      setLinkingUser(true);

      // Quitar userId del instructor
      await updateDoc(doc(db, 'instructors', detailInstructor.id), {
        userId: null,
        updatedAt: serverTimestamp()
      });

      // Cambiar rol del usuario a 'user' y quitar instructorId
      await updateDoc(doc(db, 'users', userId), {
        role: 'user',
        instructorId: null,
        updatedAt: serverTimestamp()
      });

      // Actualizar estado local
      setInstructors(prev => prev.map(inst =>
        inst.id === detailInstructor.id
          ? { ...inst, userId: undefined } as Instructor
          : inst
      ));
      setDetailInstructor(prev => {
        if (!prev) return null;
        const updated = { ...prev };
        delete (updated as any).userId;
        return updated;
      });
      setLinkedUserData(null);

      toast.success('Usuario desvinculado correctamente');

    } catch (error) {
      console.error('Error desvinculando usuario:', error);
      toast.error('Error al desvincular el usuario');
    } finally {
      setLinkingUser(false);
    }
  };

  const handleCreateInstructor = () => {
    // Validar límite de instructores según el plan
    if (currentBusiness?.subscription?.plan) {
      const plan = currentBusiness.subscription.plan as 'free' | 'basic' | 'pro';
      const maxInstructors = systemConfig.plans[plan]?.maxInstructors ?? 1;

      // Contar instructores activos (o todos, según política. Normalmente se cuentan todos los creados)
      // Si maxInstructors es -1, es ilimitado
      if (maxInstructors !== -1 && instructors.length >= maxInstructors) {
        toast.error(`Tu plan actual (${systemConfig.plans[plan]?.name}) solo permite ${maxInstructors} instructor(es). Actualiza tu plan para añadir más.`);
        return;
      }
    }

    setShowCreateModal(true);
  };

  const handleEditInstructor = (instructor: Instructor) => {
    setSelectedInstructor(instructor);
    setFormData({
      firstName: instructor.firstName,
      lastName: instructor.lastName,
      email: instructor.email,
      phone: instructor.phone || '',
      bio: instructor.bio || '',
      specialtiesText: (instructor.specialties || []).join(', '),
      experience: instructor.experience,
      certificationsText: (instructor.certifications || []).join(', '),
      isActive: instructor.isActive,
      classTypeIds: instructor.classTypeIds || [],
    });
    setAvatarPreview(instructor.avatar || '');
    setAvatarFile(null);
    setShowEditModal(true);
  };

  const handleDeleteInstructor = async (instructorId: string) => {
    if (confirm('¿Estás seguro de que quieres eliminar este instructor?')) {
      try {
        await deleteDoc(doc(db, 'instructors', instructorId));
        toast.success('Instructor eliminado exitosamente');
        loadInstructors();
      } catch (error) {
        console.error('Error eliminando instructor:', error);
        toast.error('Error al eliminar el instructor');
      }
    }
  };

  const createInstructor = async () => {
    if (!businessId) return;

    try {
      if (!formData.firstName || !formData.lastName) {
        toast.error('Por favor completa el nombre y apellido');
        return;
      }

      // Subir foto si se seleccionó
      let avatarUrl: string | undefined = undefined;
      if (avatarFile) {
        const compressedFile = await compressImage(avatarFile);
        const path = `instructors/${businessId}/${Date.now()}_${compressedFile.name}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, compressedFile);
        avatarUrl = await getDownloadURL(storageRef);
      }

      // Convertir texto a arrays
      const specialtiesArray = formData.specialtiesText
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);
      const certificationsArray = formData.certificationsText
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);

      // Limpiar campos undefined antes de enviar a Firebase
      const cleanFormData = {
        firstName: formData.firstName || '',
        lastName: formData.lastName || '',
        email: formData.email || '',
        phone: formData.phone || '',
        bio: formData.bio || '',
        specialties: specialtiesArray,
        experience: formData.experience || 0,
        certifications: certificationsArray,
        isActive: formData.isActive,
        status: formData.isActive ? 'active' : 'inactive', // Para compatibilidad con showcase
        hireDate: new Date().toISOString().split('T')[0],
        rating: 0,
        totalClasses: 0,
        totalStudents: 0,
        avatar: avatarUrl || '', // Usar string vacío si no hay avatar
        classTypeIds: formData.classTypeIds || [],
        businessId, // Agregar businessId
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      console.log('🔄 Creando instructor con datos limpios:', cleanFormData);

      await addDoc(collection(db, 'instructors'), cleanFormData);
      toast.success('Instructor creado exitosamente');
      setShowCreateModal(false);
      resetForm();
      loadInstructors();
    } catch (error) {
      console.error('Error creando instructor:', error);
      toast.error('Error al crear el instructor');
    }
  };

  const updateInstructor = async () => {
    try {
      if (!selectedInstructor) return;

      // Subir nueva foto si se seleccionó
      let avatarUrl = selectedInstructor.avatar || '';
      if (avatarFile) {
        const compressedFile = await compressImage(avatarFile);
        const path = `instructors/${selectedInstructor.businessId || 'common'}/${Date.now()}_${compressedFile.name}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, compressedFile);
        avatarUrl = await getDownloadURL(storageRef);
      }

      // Convertir texto a arrays
      const specialtiesArray = formData.specialtiesText
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);
      const certificationsArray = formData.certificationsText
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);

      // Limpiar campos undefined antes de enviar a Firebase
      const cleanFormData = {
        firstName: formData.firstName || '',
        lastName: formData.lastName || '',
        email: formData.email || '',
        phone: formData.phone || '',
        bio: formData.bio || '',
        specialties: specialtiesArray,
        experience: formData.experience || 0,
        certifications: certificationsArray,
        isActive: formData.isActive,
        status: formData.isActive ? 'active' : 'inactive', // Para compatibilidad con showcase
        avatar: avatarUrl || '', // Usar string vacío si no hay avatar
        classTypeIds: formData.classTypeIds || [],
        updatedAt: serverTimestamp()
      };

      console.log('🔄 Actualizando instructor con datos limpios:', cleanFormData);

      await updateDoc(doc(db, 'instructors', selectedInstructor.id), cleanFormData);
      toast.success('Instructor actualizado exitosamente');
      setShowEditModal(false);
      setSelectedInstructor(null);
      resetForm();
      loadInstructors();
    } catch (error) {
      console.error('Error actualizando instructor:', error);
      toast.error('Error al actualizar el instructor');
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      bio: '',
      specialtiesText: '',
      experience: 0,
      certificationsText: '',
      isActive: true,
      classTypeIds: []
    });
    setAvatarFile(null);
    setAvatarPreview('');
  };

  // Toggle selección de clase
  const toggleClassType = (classTypeId: string) => {
    setFormData(prev => ({
      ...prev,
      classTypeIds: prev.classTypeIds.includes(classTypeId)
        ? prev.classTypeIds.filter(id => id !== classTypeId)
        : [...prev.classTypeIds, classTypeId]
    }));
  };

  // Obtener nombres de clases por IDs
  const getClassTypeNames = (ids: string[]) => {
    return classTypes
      .filter(ct => ids.includes(ct.id))
      .map(ct => ct.name);
  };

  const filteredInstructors = instructors.filter(instructor => {
    if (filter === 'active') return instructor.isActive;
    if (filter === 'inactive') return !instructor.isActive;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loading size="lg" className="mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Cargando instructores...</p>
        </div>
      </div>
    );
  }

  if (!businessId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center text-gray-600 dark:text-gray-400">
          <p>No hay un negocio activo seleccionado.</p>
          <p className="text-sm">Accede como administrador de un negocio para gestionar instructores.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-amber-500">
            Gestión de Instructores
          </h1>
          <p className="text-gray-400 mt-2">
            Administra el personal docente y sus especialidades
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleCreateInstructor}>
            <Plus className="w-4 h-4 mr-2" />
            Agregar Instructor
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex gap-2">
        <Button
          variant={filter === 'all' ? 'primary' : 'outline'}
          onClick={() => setFilter('all')}
        >
          Todos ({instructors.length})
        </Button>
        <Button
          variant={filter === 'active' ? 'primary' : 'outline'}
          onClick={() => setFilter('active')}
        >
          Activos ({instructors.filter(i => i.isActive).length})
        </Button>
        <Button
          variant={filter === 'inactive' ? 'primary' : 'outline'}
          onClick={() => setFilter('inactive')}
        >
          Inactivos ({instructors.filter(i => !i.isActive).length})
        </Button>
      </div>

      {/* Lista de instructores */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filteredInstructors.map((instructor) => (
          <div
            key={instructor.id}
            className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700 hover:border-primary-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-primary-500/10 cursor-pointer"
            onClick={() => handleViewInstructorDetails(instructor)}
          >
            {/* Header con foto grande */}
            <div className="relative h-52">
              {instructor.avatar ? (
                <img
                  src={instructor.avatar}
                  alt={`${instructor.firstName} ${instructor.lastName}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-primary-600/30 to-primary-900/30 flex items-center justify-center">
                  <User className="w-20 h-20 text-gray-500" />
                </div>
              )}
              {/* Overlay gradiente */}
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent" />

              {/* Botones de acción */}
              <div className="absolute top-3 right-3 flex gap-1">
                <button
                  onClick={(e) => { e.stopPropagation(); handleEditInstructor(instructor); }}
                  className="p-2 rounded-lg bg-gray-900/60 backdrop-blur-sm text-white hover:bg-primary-500 transition-colors"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteInstructor(instructor.id); }}
                  className="p-2 rounded-lg bg-gray-900/60 backdrop-blur-sm text-white hover:bg-red-500 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              {/* Badge de estado */}
              <div className="absolute top-3 left-3">
                <span className={`px-2 py-1 text-xs font-medium rounded-full backdrop-blur-sm ${instructor.isActive
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                  : 'bg-red-500/20 text-red-400 border border-red-500/30'
                  }`}>
                  {instructor.isActive ? 'Activo' : 'Inactivo'}
                </span>
              </div>

              {/* Nombre superpuesto en la imagen */}
              <div className="absolute bottom-3 left-4 right-4">
                <h3 className="text-xl font-bold text-white drop-shadow-lg">
                  {instructor.firstName} {instructor.lastName}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <Star className="w-4 h-4 text-amber-400" />
                  <span className="text-sm text-gray-200">{(instructor.rating || 0).toFixed(1)}</span>
                  <span className="text-gray-400 mx-1">•</span>
                  <Award className="w-4 h-4 text-primary-400" />
                  <span className="text-sm text-gray-200">{instructor.experience || 0} años</span>
                </div>
              </div>
            </div>

            {/* Contenido */}
            <div className="px-4 py-3">
              {/* Info de contacto */}
              <div className="space-y-3 mb-5">
                <div className="flex items-center text-sm text-gray-300">
                  <Mail className="w-4 h-4 mr-3 text-primary-400" />
                  <span className="truncate font-medium">{instructor.email}</span>
                </div>
                {instructor.phone && (
                  <div className="flex items-center text-sm text-gray-300">
                    <Phone className="w-4 h-4 mr-3 text-primary-400" />
                    <span className="font-medium">{instructor.phone}</span>
                  </div>
                )}
              </div>

              {/* Especialidades */}
              {instructor.specialties && instructor.specialties.length > 0 && (
                <div className="mb-5">
                  <div className="flex flex-wrap gap-1.5">
                    {instructor.specialties.slice(0, 3).map((specialty, index) => (
                      <span
                        key={index}
                        className="px-2.5 py-1 text-xs font-medium bg-gray-700/50 text-gray-200 rounded-lg border border-gray-600"
                      >
                        {specialty}
                      </span>
                    ))}
                    {instructor.specialties.length > 3 && (
                      <span className="px-2 py-1 text-xs font-medium bg-gray-700 text-gray-300 rounded-lg">
                        +{instructor.specialties.length - 3}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Estadísticas */}
              <div className="grid grid-cols-3 gap-2 p-3 bg-gray-900/50 rounded-xl border border-gray-700/50">
                <div className="text-center">
                  <div className="text-lg font-bold text-white">
                    {instructor.totalClasses || 0}
                  </div>
                  <div className="text-xs font-medium text-gray-400">
                    Clases
                  </div>
                </div>
                <div className="text-center border-x border-gray-700">
                  <div className="text-lg font-bold text-white">
                    0
                  </div>
                  <div className="text-xs font-medium text-gray-400">
                    Próximas
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-white">
                    {instructor.totalStudents || 0}
                  </div>
                  <div className="text-xs font-medium text-gray-400">
                    Estudiantes
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal para crear instructor */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-white">Crear Nuevo Instructor</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nombre *
                  </label>
                  <Input
                    value={formData.firstName}
                    onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                    placeholder="Nombre"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Apellido *
                  </label>
                  <Input
                    value={formData.lastName}
                    onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                    placeholder="Apellido"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Foto (opcional)
                </label>
                <div className="flex items-center gap-4">
                  {avatarPreview ? (
                    <div className="relative">
                      <img src={avatarPreview} alt="Preview" className="w-32 h-32 rounded-2xl object-cover border-4 border-primary-500 shadow-lg shadow-primary-500/20" />
                      <button
                        type="button"
                        onClick={() => {
                          setAvatarFile(null);
                          setAvatarPreview('');
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 shadow-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-32 h-32 rounded-2xl bg-gray-700 border-2 border-dashed border-gray-600 flex items-center justify-center">
                      <User className="w-12 h-12 text-gray-500" />
                    </div>
                  )}
                  <label className="flex-1">
                    <span className="block px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-300 text-sm cursor-pointer hover:bg-gray-600 transition-colors text-center">
                      Seleccionar imagen
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        setAvatarFile(f || null);
                        setAvatarPreview(f ? URL.createObjectURL(f) : '');
                      }}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="email@ejemplo.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Teléfono
                  </label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+34 600 123 456"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Biografía
                </label>
                <textarea
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  rows={3}
                  value={formData.bio}
                  onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                  placeholder="Descripción del instructor..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Años de Experiencia
                  </label>
                  <Input
                    type="number"
                    value={formData.experience}
                    onChange={(e) => setFormData(prev => ({ ...prev, experience: parseInt(e.target.value) || 0 }))}
                    min="0"
                    max="50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Especialidades
                  </label>
                  <Input
                    value={formData.specialtiesText}
                    onChange={(e) => setFormData(prev => ({ ...prev, specialtiesText: e.target.value }))}
                    placeholder="Karate, Jiu-Jitsu..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Certificaciones (separadas por comas)
                </label>
                <Input
                  value={formData.certificationsText}
                  onChange={(e) => setFormData(prev => ({ ...prev, certificationsText: e.target.value }))}
                  placeholder="Ej: Cinturón Negro 3º Dan, Instructor Certificado"
                />
              </div>

              {/* Selector de clases */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Clases que imparte
                </label>
                {classTypes.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">
                    No hay clases creadas. Crea clases primero en "Clases".
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2 p-3 border border-gray-600 rounded-lg bg-gray-700/50 max-h-40 overflow-y-auto">
                    {classTypes.map(ct => (
                      <button
                        key={ct.id}
                        type="button"
                        onClick={() => toggleClassType(ct.id)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${formData.classTypeIds.includes(ct.id)
                          ? 'bg-primary-500 text-white shadow-md'
                          : 'bg-gray-600 text-gray-300 border border-gray-500 hover:border-primary-400'
                          }`}
                      >
                        {ct.name}
                      </button>
                    ))}
                  </div>
                )}
                {formData.classTypeIds.length > 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    {formData.classTypeIds.length} clase(s) seleccionada(s)
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={createInstructor} className="flex-1">
                Crear Instructor
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para editar instructor */}
      {showEditModal && selectedInstructor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-white">Editar Instructor</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedInstructor(null);
                  resetForm();
                }}
                className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nombre *
                  </label>
                  <Input
                    value={formData.firstName}
                    onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                    placeholder="Nombre"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Apellido *
                  </label>
                  <Input
                    value={formData.lastName}
                    onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                    placeholder="Apellido"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Foto (opcional)
                </label>
                <div className="flex items-center gap-4">
                  {(avatarPreview || selectedInstructor?.avatar) ? (
                    <div className="relative">
                      <img
                        src={avatarPreview || selectedInstructor?.avatar || ''}
                        alt="Preview"
                        className="w-32 h-32 rounded-2xl object-cover border-4 border-primary-500 shadow-lg shadow-primary-500/20"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setAvatarFile(null);
                          setAvatarPreview('');
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 shadow-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-32 h-32 rounded-2xl bg-gray-700 border-2 border-dashed border-gray-600 flex items-center justify-center">
                      <User className="w-12 h-12 text-gray-500" />
                    </div>
                  )}
                  <label className="flex-1">
                    <span className="block px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-300 text-sm cursor-pointer hover:bg-gray-600 transition-colors text-center">
                      Cambiar imagen
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        setAvatarFile(f || null);
                        setAvatarPreview(f ? URL.createObjectURL(f) : (selectedInstructor?.avatar || ''));
                      }}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="email@ejemplo.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Teléfono
                  </label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+34 600 123 456"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Biografía
                </label>
                <textarea
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  rows={3}
                  value={formData.bio}
                  onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                  placeholder="Descripción del instructor..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Años de Experiencia
                  </label>
                  <Input
                    type="number"
                    value={formData.experience}
                    onChange={(e) => setFormData(prev => ({ ...prev, experience: parseInt(e.target.value) || 0 }))}
                    min="0"
                    max="50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Especialidades
                  </label>
                  <Input
                    value={formData.specialtiesText}
                    onChange={(e) => setFormData(prev => ({ ...prev, specialtiesText: e.target.value }))}
                    placeholder="Karate, Jiu-Jitsu..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Certificaciones (separadas por comas)
                </label>
                <Input
                  value={formData.certificationsText}
                  onChange={(e) => setFormData(prev => ({ ...prev, certificationsText: e.target.value }))}
                  placeholder="Cinturón Negro, Instructor Certificado"
                />
              </div>

              {/* Selector de clases */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Clases que imparte
                </label>
                {classTypes.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">
                    No hay clases creadas. Crea clases primero en "Clases".
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2 p-3 border border-gray-600 rounded-lg bg-gray-700/50 max-h-40 overflow-y-auto">
                    {classTypes.map(ct => (
                      <button
                        key={ct.id}
                        type="button"
                        onClick={() => toggleClassType(ct.id)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${formData.classTypeIds.includes(ct.id)
                          ? 'bg-primary-500 text-white shadow-md'
                          : 'bg-gray-600 text-gray-300 border border-gray-500 hover:border-primary-400'
                          }`}
                      >
                        {ct.name}
                      </button>
                    ))}
                  </div>
                )}
                {formData.classTypeIds.length > 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    {formData.classTypeIds.length} clase(s) seleccionada(s)
                  </p>
                )}
              </div>

              {/* Estado activo */}
              <div className="flex items-center p-3 bg-gray-700/50 rounded-lg border border-gray-600">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="w-4 h-4 text-primary-500 bg-gray-700 border-gray-600 rounded focus:ring-primary-500"
                />
                <label htmlFor="isActive" className="ml-3 text-sm font-medium text-gray-300">
                  Instructor Activo
                </label>
                <span className={`ml-auto px-2 py-1 text-xs rounded-full ${formData.isActive
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                  : 'bg-red-500/20 text-red-400 border border-red-500/30'
                  }`}>
                  {formData.isActive ? 'Activo' : 'Inactivo'}
                </span>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={updateInstructor} className="flex-1">
                Guardar Cambios
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedInstructor(null);
                  resetForm();
                }}
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Panel lateral de detalles del instructor */}
      {showDetailPanel && detailInstructor && (
        <div className="fixed inset-0 bg-black/60 z-50 flex justify-end">
          {/* Overlay para cerrar */}
          <div
            className="flex-1"
            onClick={() => setShowDetailPanel(false)}
          />

          {/* Panel */}
          <div className="w-full max-w-lg bg-gray-900 border-l border-gray-800 h-full overflow-y-auto animate-slide-in-right">
            {/* Header */}
            <div className="sticky top-0 bg-gray-900 border-b border-gray-800 p-4 z-10">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">Detalles del Instructor</h2>
                <button
                  onClick={() => setShowDetailPanel(false)}
                  className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Info del instructor */}
            <div className="p-4 border-b border-gray-800">
              <div className="flex flex-col items-center gap-4">
                {detailInstructor.avatar ? (
                  <img
                    src={detailInstructor.avatar}
                    alt={`${detailInstructor.firstName} ${detailInstructor.lastName}`}
                    className="w-32 h-32 rounded-2xl object-cover border-4 border-primary-500 shadow-lg shadow-primary-500/20"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-2xl bg-gray-800 flex items-center justify-center border-2 border-gray-700">
                    <User className="w-12 h-12 text-gray-500" />
                  </div>
                )}
                <div className="text-center">
                  <h3 className="text-xl font-bold text-white">
                    {detailInstructor.firstName} {detailInstructor.lastName}
                  </h3>
                  <span className={`inline-block mt-2 px-3 py-1 text-sm font-medium rounded-full ${detailInstructor.isActive
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : 'bg-red-500/20 text-red-400 border border-red-500/30'
                    }`}>
                    {detailInstructor.isActive ? 'Activo' : 'Inactivo'}
                  </span>
                  <div className="flex items-center justify-center gap-2 mt-2 text-sm text-gray-400">
                    <Mail className="w-4 h-4" />
                    {detailInstructor.email}
                  </div>
                  {detailInstructor.phone && (
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
                      <Phone className="w-4 h-4" />
                      {detailInstructor.phone}
                    </div>
                  )}
                </div>
              </div>

              {/* Estadísticas rápidas */}
              <div className="grid grid-cols-3 gap-3 mt-4">
                <div className="bg-gray-800 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-primary-400">{detailInstructor.totalClasses || 0}</div>
                  <div className="text-xs text-gray-500">Clases totales</div>
                </div>
                <div className="bg-gray-800 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-amber-400">{detailInstructor.totalStudents || 0}</div>
                  <div className="text-xs text-gray-500">Estudiantes</div>
                </div>
                <div className="bg-gray-800 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-400 flex items-center justify-center gap-1">
                    <Star className="w-4 h-4" />
                    {(detailInstructor.rating || 0).toFixed(1)}
                  </div>
                  <div className="text-xs text-gray-500">Valoración</div>
                </div>
              </div>
            </div>

            {/* Vinculación de cuenta de usuario */}
            <div className="p-4 border-b border-gray-800">
              <div className="flex items-center gap-2 mb-3">
                <UserCheck className="w-5 h-5 text-primary-400" />
                <h3 className="text-lg font-semibold text-white">Cuenta de Usuario</h3>
              </div>

              {(detailInstructor as any).userId && linkedUserData ? (
                // Usuario vinculado
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                        <UserCheck className="w-5 h-5 text-green-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">
                          {linkedUserData.firstName} {linkedUserData.lastName}
                        </p>
                        <p className="text-sm text-gray-400">{linkedUserData.email}</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleUnlinkUser}
                      disabled={linkingUser}
                      className="text-red-400 border-red-500/30 hover:bg-red-500/10"
                    >
                      <Unlink className="w-4 h-4 mr-1" />
                      {linkingUser ? 'Desvinculando...' : 'Desvincular'}
                    </Button>
                  </div>
                  <p className="text-xs text-green-400 mt-3">
                    ✓ Este instructor puede acceder al panel de instructor con esta cuenta
                  </p>
                </div>
              ) : (
                // Sin usuario vinculado - mostrar formulario de búsqueda
                <div className="space-y-3">
                  <p className="text-sm text-gray-400">
                    Vincula una cuenta de usuario para que pueda acceder al panel de instructor.
                  </p>

                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        value={linkEmail}
                        onChange={(e) => setLinkEmail(e.target.value)}
                        placeholder="Buscar por email del usuario..."
                        className="pl-10"
                        onKeyDown={(e) => e.key === 'Enter' && handleSearchUser()}
                      />
                    </div>
                    <Button
                      onClick={handleSearchUser}
                      disabled={searchingUser || !linkEmail.trim()}
                    >
                      {searchingUser ? 'Buscando...' : 'Buscar'}
                    </Button>
                  </div>

                  {linkedUserData && !(detailInstructor as any).userId && (
                    <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                            <User className="w-5 h-5 text-primary-400" />
                          </div>
                          <div>
                            <p className="font-medium text-white">
                              {linkedUserData.firstName} {linkedUserData.lastName}
                            </p>
                            <p className="text-sm text-gray-400">{linkedUserData.email}</p>
                          </div>
                        </div>
                        <Button
                          onClick={handleLinkUser}
                          disabled={linkingUser}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Link className="w-4 h-4 mr-1" />
                          {linkingUser ? 'Vinculando...' : 'Vincular'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Anotaciones */}
            <div className="p-4 border-b border-gray-800">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-5 h-5 text-primary-400" />
                <h3 className="text-lg font-semibold text-white">Anotaciones</h3>
              </div>
              <textarea
                value={instructorNotes}
                onChange={(e) => setInstructorNotes(e.target.value)}
                placeholder="Añade notas sobre este instructor (ej: disponibilidad, preferencias, observaciones...)"
                rows={4}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              />
              <Button
                onClick={handleSaveNotes}
                disabled={savingNotes}
                className="mt-2 w-full bg-primary-500 hover:bg-primary-600"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingNotes ? 'Guardando...' : 'Guardar Anotaciones'}
              </Button>
            </div>

            {/* Historial de clases */}
            <div className="p-4">
              <div className="flex items-center gap-2 mb-4">
                <History className="w-5 h-5 text-primary-400" />
                <h3 className="text-lg font-semibold text-white">Historial de Clases</h3>
                <span className="ml-auto text-sm text-gray-500">{classHistory.length} clases</span>
              </div>

              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                </div>
              ) : classHistory.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                  <p className="text-gray-500">No hay clases registradas</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {classHistory.map((cls) => {
                    const classDate = new Date(`${cls.date}T${cls.time}`);
                    const isPast = classDate < new Date();
                    const isToday = cls.date === new Date().toISOString().split('T')[0];

                    return (
                      <div
                        key={cls.id}
                        className={`p-3 rounded-lg border ${isToday
                          ? 'bg-primary-500/10 border-primary-500/30'
                          : isPast
                            ? 'bg-gray-800/50 border-gray-700/50'
                            : 'bg-gray-800 border-gray-700'
                          }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-white flex items-center gap-2">
                              {cls.name}
                              {isToday && (
                                <span className="px-1.5 py-0.5 text-[10px] bg-primary-500 text-white rounded">HOY</span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-sm text-gray-400">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(cls.date).toLocaleDateString('es-ES', {
                                  weekday: 'short',
                                  day: 'numeric',
                                  month: 'short'
                                })}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {cls.time}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="flex items-center gap-1 text-sm">
                              <Users className="w-3 h-3 text-gray-500" />
                              <span className={`font-medium ${cls.enrolled >= cls.maxCapacity ? 'text-red-400' : 'text-green-400'
                                }`}>
                                {cls.enrolled}/{cls.maxCapacity}
                              </span>
                            </div>
                            {cls.location && (
                              <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {cls.location}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )
      }
    </div >
  );
};

export default InstructorsPage; 